import type { FC, ReactNode } from 'react';
import { 
  Gauge, Award, Network, Brain, MessageSquare, Tag, Timer, BarChart, CheckCircle, Circle
} from 'lucide-react';
import { Tooltip } from './Tooltip';

interface BadgeProps {
  children: ReactNode;
  className: string;
  label?: string;
}

const Badge: FC<BadgeProps> = ({ children, className, label }) => {
  return (
    <span className={`inline-flex items-center font-semibold rounded-full px-2 py-0.5 text-[10px] leading-none ${className}`}>
      {children}
      {label && <span className="hidden sm:inline ml-1">{label}</span>}
    </span>
  );
};

interface DifficultyBadgeProps {
  difficulty: string;
}

export const DifficultyBadge: FC<DifficultyBadgeProps> = ({ difficulty }) => {
  const difficultyStyles: { [key: string]: { icon: ReactNode; className: string; label: string } } = {
    'baixo': { icon: <Gauge size={10} />, className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Baixo' },
    'médio': { icon: <Gauge size={10} />, className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', label: 'Médio' },
    'alto': { icon: <Gauge size={10} />, className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', label: 'Alto' },
    // Fallbacks para compatibilidade
    'Fácil': { icon: <Gauge size={10} />, className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Fácil' },
    'Médio': { icon: <Gauge size={10} />, className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', label: 'Médio' },
    'Avançado': { icon: <Gauge size={10} />, className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', label: 'Avançado' },
  };
  const style = difficultyStyles[difficulty] || { icon: <Gauge size={10} />, className: 'bg-gray-100 text-gray-800', label: difficulty };

  return (
    <Tooltip content={style.label}>
      <Badge className={style.className} label={style.label}>
        {style.icon}
      </Badge>
    </Tooltip>
  );
};

interface CategoryBadgeProps {
  category: string;
}

export const CategoryBadge: FC<CategoryBadgeProps> = ({ category }) => {
  const categoryStyles: { [key: string]: { icon: ReactNode; className: string, label: string } } = {
    // Valores originais do JSON
    'comunicação': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Comunicação' },
    'oratória': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Oratória' },
    'liderança': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Liderança' },
    'gestão de pessoas': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Gestão de Pessoas' },
    'inteligência emocional': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Inteligência Emocional' },
    'relacionamento interpessoal': { icon: <Network size={10} />, className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300', label: 'Relacionamento' },
    'autoconhecimento': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Autoconhecimento' },
    'negociação': { icon: <MessageSquare size={10} />, className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300', label: 'Negociação' },
    'gestão de projetos': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Gestão de Projetos' },
    'tomada de decisão': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Tomada de Decisão' },
    'análise de dados': { icon: <BarChart size={10} />, className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300', label: 'Análise de Dados' },
    'mindset': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Mindset' },
    'gestão do tempo': { icon: <Timer size={10} />, className: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300', label: 'Gestão do Tempo' },
    'planejamento estratégico': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Planejamento Estratégico' },
    'assertividade': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Assertividade' },
    'comunicação pessoal': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Comunicação Pessoal' },
    'comunicação organizacional': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Comunicação Organizacional' },
    'agilidade': { icon: <Timer size={10} />, className: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300', label: 'Agilidade' },
    'participação ativa': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Participação Ativa' },
    'inovação': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Inovação' },
    'liderança adaptativa': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Liderança Adaptativa' },
    'liderança em inovação': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Liderança em Inovação' },
    'desenvolvimento profissional': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Desenvolvimento Profissional' },
    'posicionamento profissional': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Posicionamento Profissional' },
    'liderança de desenvolvimento': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Liderança de Desenvolvimento' },
    'desenvolvimento contínuo': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Desenvolvimento Contínuo' },
    'marca pessoal': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Marca Pessoal' },
    'desenvolvimento técnico': { icon: <BarChart size={10} />, className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300', label: 'Desenvolvimento Técnico' },
    'autopromoção': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Autopromoção' },
    'reconhecimento de competência': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Reconhecimento de Competência' },
    'comunicação espontânea': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Comunicação Espontânea' },
    'gestão de equipes': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Gestão de Equipes' },
    'liderança colaborativa': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Liderança Colaborativa' },
    'autenticidade': { icon: <Brain size={10} />, className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', label: 'Autenticidade' },
    
    // Fallbacks para compatibilidade com valores antigos
    'communication': { icon: <MessageSquare size={10} />, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Comunicação' },
    'leadership': { icon: <Award size={10} />, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Liderança' },
    'networking': { icon: <Network size={10} />, className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300', label: 'Networking' },
  };
  const style = categoryStyles[category] || { icon: <Tag size={10} />, className: 'bg-gray-100 text-gray-800', label: category };

  return (
    <Tooltip content={style.label}>
      <Badge className={style.className} label={style.label}>
        {style.icon}
      </Badge>
    </Tooltip>
  );
};

interface DueDateBadgeProps {
  dueDate: Date | null;
}

export const DueDateBadge: FC<DueDateBadgeProps> = ({ dueDate }) => {
  if (!dueDate) return null;

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const localDueDate = new Date(dueDate);
  
  const diffTime = localDueDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let style = { className: '', label: '', icon: <Timer size={10} /> };

  if (diffDays < 0) {
    style = { 
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', 
      label: `Atrasado ${Math.abs(diffDays)}d`, 
      icon: <Timer size={10} /> 
    };
  } else if (diffDays === 0) {
    style = { 
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', 
      label: 'Vence hoje', 
      icon: <Timer size={10} /> 
    };
  } else if (diffDays <= 3) {
    style = { 
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', 
      label: `Vence em ${diffDays}d`, 
      icon: <Timer size={10} /> 
    };
  } else {
    return null; // Don't show badge if due date is far
  }

  return (
    <Tooltip content={`Prazo: ${localDueDate.toLocaleDateString()}`}>
      <Badge className={style.className} label={style.label}>
        {style.icon}
      </Badge>
    </Tooltip>
  );
};

interface ImpactBadgeProps {
  impact: string;
}

export const ImpactBadge: FC<ImpactBadgeProps> = ({ impact }) => {
    const impactStyles: { [key: string]: { icon: ReactNode; className: string, label: string } } = {
        'Alto': { icon: <BarChart size={12} className="mr-1" />, className: 'bg-primary-100 text-primary', label: 'Alto Impacto' },
        'Médio': { icon: <BarChart size={12} className="mr-1" />, className: 'bg-neutral-200 text-neutral-600', label: 'Médio Impacto' },
        'Baixo': { icon: <BarChart size={12} className="mr-1" />, className: 'bg-secondary-100 text-secondary', label: 'Baixo Impacto' },
    };
    const style = impactStyles[impact] || { icon: <BarChart size={12} className="mr-1" />, className: 'bg-gray-100 text-gray-800', label: impact };
    return <Badge className={style.className}>{style.icon}{style.label}</Badge>;
};

interface StatusBadgeProps {
  completed: boolean;
}

export const StatusBadge: FC<StatusBadgeProps> = ({ completed }) => {
    const style = completed 
        ? { icon: <CheckCircle size={12} className="mr-1"/>, className: 'bg-green-100 text-green-700', label: 'Concluída' }
        : { icon: <Circle size={12} className="mr-1"/>, className: 'bg-gray-100 text-gray-700', label: 'Pendente' };
    return <Badge className={style.className}>{style.icon}{style.label}</Badge>;
}; 